"use client";

import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react"; // Import useMemo and useRef

import { IconHomeOff } from "@tabler/icons-react";
import { UseEmblaCarouselType } from "embla-carousel-react";
import { useTranslations } from "next-intl";

import ResponsiveImage from "@/components/ui/ResponsiveImage";
import { EnumModalName } from "@/enums/modal-state";
import { useModalStore } from "@/store/modal.store";
import { useSliderStore } from "@/store/slider.store";
import { IImagePopulated, IImagesProperty } from "@/types/images.types";
import { CustomSlider } from "@/ui/Common/CustomSlider";
import { cn } from "@/utils/cn";

import iconEmpty from "public/assets/img/empty.png";

import styles from "./styles.module.scss";

// Fallback image component
const FallbackImage = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "flex items-center justify-center bg-gray-100 h-full w-full",
      className,
    )}
  >
    <IconHomeOff
      size={100}
      stroke={1}
      className="max-w-[50%] h-auto text-gray-200"
    />
  </div>
);

// Main image component for slider
const SliderImage = ({
  image,
  index,
  title,
  onImageClick,
}: {
  image: IImagePopulated[];
  index: number;
  title: string;
  onImageClick: (index: number) => void;
}) => {
  const isFirstElement = index === 0;
  const hasValidImageUrl = image && image.length > 0;

  if (!hasValidImageUrl) {
    return <FallbackImage className={styles.itemImages} />;
  }

  return (
    <ResponsiveImage
      images={image}
      className={cn("object-cover")}
      maxSize="table"
      // fill={true}
      alt={title}
      onClick={() => onImageClick(index)}
      priority={isFirstElement}
      loading={isFirstElement ? "eager" : "lazy"}
    />
  );
};

const ThumbnailImage = ({
  image,
  index,
  title,
  isActive,
  onClick,
}: {
  image: IImagePopulated[];
  index: number;
  title: string;
  isActive: boolean;
  onClick: () => void;
}) => {
  const hasValidImageUrl = image && image.length > 0;

  if (!hasValidImageUrl) {
    return <FallbackImage />;
  }

  return (
    <ResponsiveImage
      images={image}
      alt={`${title} thumbnail ${index + 1}`}
      className="overflow-hidden"
      sizes="20vw"
      maxSize="mobile"
      fill={true}
    />
  );
};

interface ObjectDetailSliderProps {
  data: IImagesProperty[];
  title: string;
  className?: string;
}

export const ObjectDetailSlider = ({
  data,
  title,
  className,
}: ObjectDetailSliderProps) => {
  const t = useTranslations();

  const { openModal } = useModalStore();
  const { setSlides, setCurrentSlide, currentSlide } = useSliderStore();
  const [emblaApi, setEmblaApi] = useState<UseEmblaCarouselType[1]>();

  const thumbnailsContainerRef = useRef<HTMLDivElement>(null); // Ref for thumbnail container

  const isData = useMemo(() => !!(data.length && data.length > 0), [data]);

  const getEmblaApi = useCallback(
    (api: UseEmblaCarouselType[1] | undefined) => {
      setEmblaApi(api);
    },
    [],
  );

  const handleClick = useCallback(
    (slideIndex: number) => {
      setCurrentSlide(slideIndex);
      setSlides(data);
      openModal(EnumModalName.gallery);
    },
    [data, openModal, setSlides, setCurrentSlide],
  );

  const onThumbnailClick = useCallback(
    (index: number) => {
      if (!emblaApi) {
        return;
      }
      setCurrentSlide(index);
      emblaApi.scrollTo(index);
    },
    [emblaApi, setCurrentSlide],
  );

  useEffect(() => {
    setCurrentSlide(0);
  }, [setCurrentSlide]);

  useEffect(() => {
    if (
      thumbnailsContainerRef.current &&
      isData &&
      currentSlide !== undefined
    ) {
      const container = thumbnailsContainerRef.current;
      const activeThumbnail = container.children[currentSlide] as HTMLElement;

      if (activeThumbnail) {
        const scrollLeft =
          activeThumbnail.offsetLeft +
          activeThumbnail.offsetWidth / 2 -
          container.offsetWidth / 2;
        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    }
  }, [currentSlide, isData]);

  return (
    <div className={cn(styles.sliderWrapper, className)}>
      <CustomSlider
        className="grow shadow-[0_0_0.3rem_rgba(0,0,0,0.2)]"
        navigationPosition="center"
        showCounter={true}
        loop={true}
        counterPosition="center"
        showPagination={false}
        navigationSize="large"
        getEmblaApi={getEmblaApi}
        setCurrentSlide={setCurrentSlide}
        main={
          <div className={cn(styles.sliderContainer)}>
            {isData ? (
              data?.map((image, index) => {
                return (
                  <div
                    key={`${index}-${title}`}
                    className={"flex-[0_0_100%] relative"}
                  >
                    <SliderImage
                      image={image.populated ?? []}
                      index={index}
                      title={title}
                      onImageClick={handleClick}
                    />
                  </div>
                );
              })
            ) : (
              <Image
                className={styles.itemImages}
                style={{ objectFit: "cover" }}
                fill={true}
                src={iconEmpty}
                alt={title}
                loading="lazy"
              />
            )}
          </div>
        }
        length={isData ? data?.length : 0}
      />
      {/* Thumbnail Slider */}
      {isData && (
        <div
          ref={thumbnailsContainerRef}
          className={cn(
            "shrink-0 flex gap-[1rem] overflow-x-auto overflow-y-hidden pb-2 scroll-smooth relative scrollbar-hide",
          )}
        >
          {data.map((image, index) => {
            return (
              <div
                key={`thumb-${index}`}
                className={cn(
                  "relative cursor-pointer aspect-[4.5/3.5] shrink-0",
                  "w-[calc(20%-1rem*4/5)] xl:w-[calc(100%/7-1rem*6/7)] rounded-[1.5rem] overflow-hidden border-2 border-transparent border-solid", // 5 items on small, 7 on larger
                  {
                    "border-red": index === currentSlide,
                  },
                )}
                onClick={() => onThumbnailClick(index)}
              >
                <ThumbnailImage
                  image={image.populated ?? []}
                  index={index}
                  title={title}
                  isActive={index === currentSlide}
                  onClick={() => onThumbnailClick(index)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
